import { FilterList, Tune } from "@mui/icons-material";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import Divider from "@mui/material/Divider";
import { TinyIconGenerator } from "@qubit/autoparts";

import { MouseEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  BatchStatus,
  BatchType,
  LowercaseBatchStatus,
  OrderType
} from "~/features/batch/batch.type";
import {
  mixpanelTrack,
  useMixpanelContextName,
  useMixpanelPageName
} from "~/lib/mixpanel-tracking";
import { TempZone } from "~/redux/actions/batch";
import { FulfillmentCenterDto } from "~/types/api";

function FetchBatchFilters(props: {
  fulfillmentCenter: FulfillmentCenterDto | null;
  selectedOrderType: OrderType | null;
  selectedTempZones: TempZone[];
  selectedBatchStatuses: BatchStatus[];
  availableBatchStatuses: BatchStatus[];
  selectedBatchTypes: BatchType[];
  availableBatchTypes: BatchType[];
  onSelectOrderType: (orderType: OrderType) => void;
  onToggleZone: (zone: TempZone) => void;
  onToggleStatus: (status: BatchStatus) => void;
  onToggleBatchType: (type: BatchType) => void;
  onResetFilters: () => void;
  iconColor?: string;
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const trackedPageName = useMixpanelPageName();
  const trackedBatchFiltersContextName =
    useMixpanelContextName("Batch Filters Menu");

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    mixpanelTrack({
      trackedPageName,
      type: "Button Click",
      label: "Open Batch Filters Menu"
    });
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const showOrderTypeFilters =
    !!props.fulfillmentCenter?.clientConfiguration?.showOrderTypeFilters;

  const allStatusesSelected =
    props.selectedBatchStatuses.length >= props.availableBatchStatuses.length;

  const allTypesSelected =
    props.selectedBatchTypes.length >= props.availableBatchTypes.length;

  const filtersTempZone = [
    {
      filter: "ambient",
      translation: t("ambient")
    },
    {
      filter: "chilled",
      translation: t("chilled")
    },
    {
      filter: "frozen",
      translation: t("frozen")
    }
  ];

  // TODO: this will eventual come from and FC config
  const orderTypes = [
    { filter: "Fresh", translation: t("fresh") },
    { filter: "Shop", translation: t("shop") }
  ];

  type FiltersBatchStatuses = { filter: BatchStatus; translation: string };
  const [filtersAvailableBatchStatuses, setFiltersAvailableBatchStatuses] =
    useState<FiltersBatchStatuses[]>([]);

  type FiltersBatchTypes = { filter: BatchType; translation: string };
  const [filtersAvailableBatchTypes, setFiltersAvailableBatchTypes] = useState<
    FiltersBatchTypes[]
  >([]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state
    setFiltersAvailableBatchStatuses([]);
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
    props.availableBatchStatuses?.map((b) =>
      setFiltersAvailableBatchStatuses((current) => [
        ...current,
        { filter: b, translation: t(b.toLowerCase() as LowercaseBatchStatus) }
      ])
    );

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state
    setFiltersAvailableBatchTypes([]);
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
    props.availableBatchTypes?.map((b) =>
      setFiltersAvailableBatchTypes((current) => [
        ...current,
        { filter: b, translation: t(b.toLowerCase() as LowercaseBatchStatus) }
      ])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <IconButton
        aria-label="Batch filters menu"
        aria-controls="simple-menu"
        aria-haspopup="true"
        onClick={handleClick}
      >
        {props.selectedTempZones.length === 1 ? (
          <TinyIconGenerator
            tempZone={props.selectedTempZones[0]}
            type="Tote"
          />
        ) : (
          <Tune sx={{ color: props.iconColor || "secondary.contrastText" }} />
        )}
      </IconButton>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={!!anchorEl}
        onClose={handleClose}
      >
        {filtersTempZone.map((zone) => (
          <MenuItem
            key={zone.filter}
            onClick={() => {
              mixpanelTrack({
                trackedPageName: trackedBatchFiltersContextName,
                type: "Select Option",
                label: "Select Batch Temp Zone",
                eventProperties: {
                  "Batch Temp Zone": zone.filter
                }
              });
              props.onToggleZone(zone.filter as TempZone);
            }}
            sx={{
              minWidth: 150,
              backgroundColor: props.selectedTempZones.includes(
                zone.filter as TempZone
              )
                ? "gray.light"
                : undefined
            }}
          >
            <Box display="flex" justifyContent="space-between">
              <TinyIconGenerator
                tempZone={zone.filter as "ambient" | "chilled" | "frozen"}
                type="Tote"
                key={`${zone.filter}-option`}
                iBgColor={
                  props.selectedTempZones.includes(zone.filter as TempZone)
                    ? "gray.light"
                    : "transparent"
                }
              />
              <Box style={{ marginLeft: 10 }}>
                {zone.translation.replace(/^\w/, (c) => c.toUpperCase())}
              </Box>
            </Box>
          </MenuItem>
        ))}
        <Divider style={{ marginTop: 2, marginBottom: 2, height: 3 }} />
        {filtersAvailableBatchStatuses?.map((status) => (
          <MenuItem
            key={status.filter}
            onClick={() => {
              mixpanelTrack({
                trackedPageName: trackedBatchFiltersContextName,
                type: "Select Option",
                label: "Select Batch Status",
                eventProperties: {
                  "Batch Status": status.filter
                }
              });
              props.onToggleStatus(status.filter);
            }}
            sx={{
              backgroundColor:
                !allStatusesSelected &&
                props.selectedBatchStatuses.includes(status.filter)
                  ? "gray.light"
                  : undefined
            }}
          >
            <Box display="flex" justifyContent="space-between">
              <TinyIconGenerator
                type={"Batch Filter"}
                key={status.filter}
                iBgColor={
                  !allStatusesSelected &&
                  props.selectedBatchStatuses.includes(status.filter)
                    ? "gray.light"
                    : "transparent"
                }
              />
              <Box style={{ marginLeft: 10 }}>
                {status.translation.replace(/^\w/, (c) => c.toUpperCase())}
              </Box>
            </Box>
          </MenuItem>
        ))}
        {!!props.availableBatchTypes.length && (
          <Divider style={{ marginTop: 2, marginBottom: 2, height: 3 }} />
        )}
        {filtersAvailableBatchTypes.map((type) => (
          <MenuItem
            key={type.filter}
            onClick={() => {
              mixpanelTrack({
                trackedPageName: trackedBatchFiltersContextName,
                type: "Select Option",
                label: "Select Batch Type",
                eventProperties: {
                  "Batch Type": type.filter
                }
              });
              props.onToggleBatchType(type.filter);
            }}
            sx={{
              backgroundColor:
                !allTypesSelected &&
                props.selectedBatchTypes.includes(type.filter)
                  ? "gray.light"
                  : undefined
            }}
          >
            <Box display="flex" justifyContent="space-between">
              <TinyIconGenerator
                type={"Batch Filter"}
                key={type.filter}
                iBgColor={
                  !allTypesSelected &&
                  props.selectedBatchTypes.includes(type.filter)
                    ? "gray.light"
                    : "transparent"
                }
              />
              <Box style={{ marginLeft: 10 }}>
                {type.translation.replace(/^\w/, (c) => c.toUpperCase())}
              </Box>
            </Box>
          </MenuItem>
        ))}
        <Divider style={{ marginTop: 2, marginBottom: 2, height: 3 }} />
        {showOrderTypeFilters &&
          orderTypes.map((type) => (
            <Box key={type.filter}>
              <MenuItem
                onClick={() => {
                  mixpanelTrack({
                    trackedPageName: trackedBatchFiltersContextName,
                    type: "Select Option",
                    label: "Select Order Type",
                    eventProperties: {
                      "Order Type": type.filter
                    }
                  });
                  props.onSelectOrderType(type.filter as OrderType);
                }}
                sx={{
                  backgroundColor:
                    props.selectedOrderType === type.filter
                      ? "gray.light"
                      : undefined
                }}
              >
                <Box display="flex" justifyContent="space-between">
                  <Box display="flex" alignItems="center">
                    <FilterList
                      sx={{
                        fontSize: 18,
                        color: "black",
                        position: "relative",
                        bottom: 2,
                        right: 1
                      }}
                    />
                  </Box>
                  <Box style={{ marginLeft: 10 }}>
                    {type.translation.replace(/^\w/, (c) => c.toUpperCase())}
                  </Box>
                </Box>
              </MenuItem>
              <Divider style={{ marginTop: 2, marginBottom: 2, height: 3 }} />
            </Box>
          ))}
        <MenuItem
          key="reset filters"
          aria-label="Reset filters"
          onClick={() => {
            mixpanelTrack({
              trackedPageName: trackedBatchFiltersContextName,
              type: "Button Click",
              label: "Reset Batch Filters"
            });
            props.onResetFilters();
            handleClose();
          }}
        >
          <Box display="flex" justifyContent="space-between">
            <Box>{t("reset filters")}</Box>
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
}

export default FetchBatchFilters;
